import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { JobStatus } from "../service/job"

// Constants for select placeholders
export const TODO_NO_JOB = "no-job"
export const TODO_UNASSIGNED = "unassigned"

export interface Todo {
  id: string
  title: string
  description?: string
  isCompleted: boolean
  deadline?: Date
  priority: number
  size: number
  jobId?: string
  storeId: string
  createdById?: string
  assignedMechanicId?: string
  completedAt?: Date
  createdAt: Date
  modifiedAt: Date
  timeDifferenceFromDeadline?: number // Calculated field: positive = overdue, negative = future, 0 if same day and not yet overdue, undefined if no deadline

  // Relations
  createdBy?: {
    id: string
    firstName: string
    lastName: string
  }
  assignedMechanic?: {
    id: string
    name: string
  }
  job?: {
    id: string
    status: JobStatus
    physicalTag?: number
    customer?: {
      id: string
      firstName: string
      lastName: string | null
    }
    bike?: {
      brand: { id: string; name: string } | null
      color: string | null
    }
    isBooking: boolean
  }
}

export interface CreateTodoRequest {
  title: string
  description?: string
  deadline?: Date
  assignedMechanicId?: string
  jobId?: string
  size?: number
}

export interface UpdateTodoRequest {
  title?: string
  description?: string | null
  isCompleted?: boolean
  deadline?: Date | null
  priority?: number
  size?: number
  assignedMechanicId?: string | null
  jobId?: string | null
}

export interface AssignTodoRequest {
  assignedMechanicId?: string
}

export interface BatchCompleteTodoRequest {
  todoIds: string[]
}

export interface BatchUncompleteTodoRequest {
  todoIds: string[]
}

export interface UpdateTodoPositionRequest {
  todoId: string
  deadline: string
  priority: number
}

// Zod schemas for validation
export const CreateTodoRequestSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(255, "Title must be at most 255 characters"),
  description: z
    .string()
    .max(2000, "Description must be at most 2000 characters")
    .optional(),
  deadline: z.coerce.date<Date>().optional(),
  assignedMechanicId: z.guid("Invalid assignedMechanicId format").optional(),
  jobId: z.guid("Invalid jobId format").optional(),
  size: z
    .number()
    .min(0, "common.validation.sizeBetweenZeroAndTen" satisfies TranslationKey)
    .max(10, "common.validation.sizeBetweenZeroAndTen" satisfies TranslationKey)
    .multipleOf(0.5, "common.validation.sizeStep" satisfies TranslationKey)
    .optional(),
})

export const UpdateTodoRequestSchema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(255, "Title must be at most 255 characters")
    .optional(),
  description: z
    .string()
    .max(2000, "Description must be at most 2000 characters")
    .nullable()
    .optional(),
  isCompleted: z.boolean().optional(),
  deadline: z.coerce.date<Date>().nullable().optional(),
  priority: z.number().optional(),
  size: z
    .number()
    .min(0, "common.validation.sizeBetweenZeroAndTen" satisfies TranslationKey)
    .max(10, "common.validation.sizeBetweenZeroAndTen" satisfies TranslationKey)
    .multipleOf(0.5, "common.validation.sizeStep" satisfies TranslationKey)
    .optional(),
  assignedMechanicId: z

    .guid("Invalid assignedMechanicId format")
    .nullable()
    .optional(),
  jobId: z.guid("Invalid jobId format").nullable().optional(),
})

export const BatchCompleteTodoRequestSchema = z.object({
  todoIds: z
    .array(z.guid("Invalid todoId format"))
    .min(1, "At least one todo ID is required"),
})

export const BatchUncompleteTodoRequestSchema = z.object({
  todoIds: z
    .array(z.guid("Invalid todoId format"))
    .min(1, "At least one todo ID is required"),
})

export const UpdateTodoPositionRequestSchema = z.object({
  todoId: z.guid("Invalid todoId format"),
  deadline: z
    .string()
    .datetime({ message: "Invalid ISO 8601 datetime format" }),
  priority: z.number(),
})
